import React, { useState } from 'react';
import { FolderOpen, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

// Opens the job / property / client folder in Google Drive (creates it if needed).
export const OpenInDriveButton = ({ entity, id, className = '' }) => {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState(null);
  const param = { job: 'appraisal_id', property: 'property_id', client: 'client_id' }[entity];

  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/drive/folder-link?entity=${entity}&id=${id}`);
      if (!data.connected) toast.error('Google Drive is not connected — ask an admin to connect it.');
      else if (data.simulated) toast.message(`Test mode · folder: ${data.path}`);
      else window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not open Drive folder'); }
    finally { setBusy(false); }
  };
  const loadFiles = () => api.get(`/drive/files?${param}=${id}`).then((r) => setFiles(r.data.rows || [])).catch(() => setFiles([]));

  return (
    <div className={`inline-flex items-stretch rounded-md overflow-hidden border border-[#1A73E8]/40 ${className}`}>
      <button data-testid={`open-in-drive-${entity}-btn`} onClick={open} disabled={busy}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white text-[#1A73E8] hover:bg-[#E8F0FE] font-semibold transition-colors">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <FolderOpen size={13} />} Open in Drive
      </button>
      <Popover onOpenChange={(o) => o && loadFiles()}>
        <PopoverTrigger asChild>
          <button data-testid={`drive-files-${entity}-btn`} title="Files saved to Drive" className="px-2 bg-white text-[#1A73E8] hover:bg-[#E8F0FE] border-l border-[#1A73E8]/30 text-[11px] font-semibold">Saved</button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-3" data-testid={`drive-files-${entity}-list`}>
          <div className="text-xs font-semibold text-slate-800 mb-2">Saved to Google Drive</div>
          {files === null ? <Loader2 size={14} className="animate-spin text-slate-400" /> : !files.length ? (
            <div className="text-[11px] text-slate-400">Nothing saved yet</div>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {files.map((f) => (
                <div key={f.drive_id} className="text-[11px]">
                  <div className="flex items-center gap-1.5 font-mono text-slate-700"><FileText size={11} className="text-slate-400" />{f.name}</div>
                  <div className="pl-4 text-[10px] text-slate-400">{(f.folder_path || '').split('/').slice(-2).join(' / ')} · {f.saved_at ? new Date(f.saved_at).toLocaleString() : ''} · {f.source}{f.saved_by ? ` · ${f.saved_by}` : ''}</div>
                </div>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
